import React, { lazy, Suspense, useEffect, useMemo, useCallback, startTransition } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { Frame, Spinner } from "@shopify/polaris";
import "@shopify/polaris/build/esm/styles.css";
import { store } from "./store/store";
import Home from "./pages/Home";
import WidgetsListPage from "./pages/widgets/WidgetsListPage";
import EventRegistrationsPage from "./pages/registrations/EventRegistrationsPage";
import SettingsPage from "./pages/settings/SettingsPage";
import AllEventsPage from "./pages/events/AllEventsPage";
import PlansPage from "./pages/PlansPage";
import AppBridgeErrorBoundary from "./components/AppBridgeErrorBoundary";
import AppBridgeProvider from "./components/AppBridgeProvider";
import NavigationMenuComponent from "./components/NavigationMenu";
import InstallGate from "./components/InstallGate";
import { AppSessionProvider } from "./context/AppSessionContext";
import { BillingProvider } from "./context/BillingContext";
import AppFooter from "./components/AppFooter";
import WhatsAppSupportFab from "./components/WhatsAppSupportFab";
import RouteSync from "./components/RouteSync";
import AppHistorySync from "./components/AppHistorySync";
import BrowserUrlSync from "./components/BrowserUrlSync";
import { ROUTER_BASENAME } from "./config/env";
import { toRouterPath } from "./utils/appPaths";

/** Widget builder is large — lazy load only create/edit routes. */
const CreateWidgetPage = lazy(() => import("./pages/widgets/CreateWidgetPage"));
const EditWidgetPage = lazy(() => import("./pages/widgets/EditWidgetPage"));

function RouteFallback() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "240px",
        width: "100%",
      }}
    >
      <Spinner accessibilityLabel="Loading page" size="large" />
    </div>
  );
}

function LegacyAppPathRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    startTransition(() => {
      navigate({ pathname: "/", search: location.search }, { replace: true });
    });
  }, [navigate, location.search]);

  return null;
}

function UnknownRouteRedirect() {
  const location = useLocation();
  const resolved = toRouterPath(location.pathname);
  if (resolved !== location.pathname && resolved.startsWith("/")) {
    return <Navigate to={{ pathname: resolved, search: location.search }} replace />;
  }
  return <Navigate to={{ pathname: "/", search: location.search }} replace />;
}

function AppContent() {
  const location = useLocation();
  const legacyFlag = useSelector((state) => state.install.installData?.shop_info?.user_type);
  const showPlans = String(legacyFlag) === "0";

  useEffect(() => {
    const prefetch = () => {
      void import("./pages/widgets/CreateWidgetPage");
      void import("./pages/widgets/EditWidgetPage");
    };
    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(prefetch, { timeout: 4000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const t = window.setTimeout(prefetch, 1500);
    return () => window.clearTimeout(t);
  }, []);

  const navigationLinks = useMemo(() => {
    const links = [
      { label: "Widgets", destination: "/widgets" },
      { label: "Events", destination: "/events" },
      { label: "Signups", destination: "/registrations" },
      { label: "Settings", destination: "/settings" },
    ];
    if (showPlans) {
      links.push({ label: "Plans & Billing", destination: "/plans" });
    }
    return links;
  }, [showPlans]);

  const matcher = useCallback(
    (link, currentLocation) => {
      const path = currentLocation.pathname;
      const dest = toRouterPath(link.destination || "/");
      if (dest === "/widgets") {
        return path === "/widgets" || path.startsWith("/widgets/");
      }
      if (dest === "/events") {
        return path === "/events" || path.startsWith("/events/");
      }
      if (dest === "/registrations") {
        return path === "/registrations" || path.startsWith("/registrations/");
      }
      if (dest === "/settings") {
        return path === "/settings" || path.startsWith("/settings/");
      }
      if (dest === "/plans") {
        return path === "/plans" || path.startsWith("/plans/");
      }
      return false;
    },
    [],
  );

  return (
    <AppBridgeProvider>
      <AppBridgeErrorBoundary>
        <Frame>
          <RouteSync />
          <AppHistorySync />
          <BrowserUrlSync />
          <NavigationMenuComponent navigationLinks={navigationLinks} matcher={matcher} />
          <div
            className="age-app-routes-root"
            style={{
              minHeight: "100%",
              minWidth: "100%",
              backgroundColor: "var(--p-color-bg, #f6f6f7)",
              boxSizing: "border-box",
            }}
          >
            <div className="age-app-main age-app-main--stable">
              <InstallGate>
                <AppSessionProvider>
                  <BillingProvider>
                  <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/widgets" element={<WidgetsListPage />} />
                  <Route path="/registrations" element={<EventRegistrationsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/plans" element={<PlansPage />} />
                  <Route path="/events" element={<AllEventsPage />} />
                  <Route
                    path="/events/new"
                    element={<Navigate to={{ pathname: "/widgets/new", search: location.search }} replace />}
                  />
                  <Route
                    path="/widgets/new"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <CreateWidgetPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/widgets/:widgetId/edit"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <EditWidgetPage />
                      </Suspense>
                    }
                  />
                  <Route path="/app" element={<LegacyAppPathRedirect />} />
                  <Route path="*" element={<UnknownRouteRedirect />} />
                  </Routes>
                  </BillingProvider>
                </AppSessionProvider>
              </InstallGate>
            </div>
            <AppFooter />
          </div>
          <WhatsAppSupportFab />
        </Frame>
      </AppBridgeErrorBoundary>
    </AppBridgeProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter basename={ROUTER_BASENAME || undefined}>
        <AppContent />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
